import { zodResolver } from '@hookform/resolvers/zod';
import { isExternalRouter } from 'auth/cognito';
import { AxiosError } from 'axios';
import { FieldInputLength } from 'components/FieldInputLength';
import { CloseFn } from 'components/misc/ExpandableForm';
import { ImagePicker } from 'components/misc/ImagePicker';
import { showErrorNotification, showNotification } from 'components/ShowNotification';
import { useCompaniesHook, UseCompaniesType } from 'hooks/skills/useCompanies';
import { useCustomerReferencesHook } from 'hooks/skills/useCustomerReferences';
import { useReferenceNotesHook } from 'hooks/skills/useReferenceNotes';
import { useMyExternalProfile, useProfile } from 'hooks/useProfile';
import { useState } from 'react';
import { Controller, FieldErrors, SubmitHandler, useForm } from 'react-hook-form';
import { Button, Checkbox, Col, Input, InputPicker, Row, Tooltip, Whisper } from 'rsuite';
import type { FileType } from 'rsuite/Uploader';
import {
    NewCustomerReferenceForm,
    NewCustomerReferenceFormType,
    referenceQuoteByFieldMaxLength,
    referenceQuoteFieldMaxLength,
    referenceTextFieldMaxLength,
} from 'schema/customer_references/reference';

import { removeCompanyNameSuffix } from '../../../utils/skills';
import { ErrorParagraph } from '../log-hours/styles';

export const CustomerReferenceAddForm = ({
    onAdded,
    refetchReferences,
}: {
    onAdded: CloseFn;
    refetchReferences: () => void;
}) => {
    const hookToUseForProfile = isExternalRouter ? useMyExternalProfile : useProfile;
    const { user } = hookToUseForProfile();
    const useCompanies = useCompaniesHook(isExternalRouter);

    const [selectedCompany, setSelectedCompany] = useState<UseCompaniesType>();
    const [selectedBackgroundImage, setSelectedBackgroundImage] = useState<FileType | undefined>(
        undefined,
    );
    const [selectedLogo, setSelectedLogo] = useState<FileType | undefined>(undefined);

    const { companies, refetch: refetchCompanies } = useCompanies({
        enabled: true,
        member_id: user?.id,
    });

    const circlesCustomers =
        companies.filter(company => company.circles_customer_id !== null) || [];

    const { createReference } = useCustomerReferencesHook();
    const { referenceNotesData, refetchReferenceNotes, isReferenceNotesLoading } =
        useReferenceNotesHook(selectedCompany?.circles_customer_id);

    const {
        formState: { errors },
        watch,
        control,
        handleSubmit,
        setValue,
    } = useForm<NewCustomerReferenceFormType>({
        resolver: zodResolver(NewCustomerReferenceForm),
    });

    const onSubmit: SubmitHandler<NewCustomerReferenceFormType> = data => {
        const customer_id = selectedCompany?.circles_customer_id;

        if (customer_id === undefined) {
            return;
        }
        if (selectedBackgroundImage?.blobFile) {
            data.image_file = selectedBackgroundImage.blobFile;
        }
        if (selectedLogo) {
            data.logo_file = selectedLogo.blobFile;
        }
        createReference.mutate(data, customer_id, {
            onSuccess: async () => {
                await Promise.all([refetchCompanies(), refetchReferences()]);
                showNotification({ header: 'New reference created successfully.' });
                onAdded();
            },
            onError: (error: AxiosError) => {
                showErrorNotification(error);
            },
        });
    };

    const onGenerateNotes = () => {
        refetchReferenceNotes();
        setValue('notes', referenceNotesData);
    };

    const onError = (errors: FieldErrors<NewCustomerReferenceFormType>) => {
        console.error('Form Errors:', errors);
    };

    const watchFields = watch();

    return (
        <form onSubmit={handleSubmit(onSubmit, onError)}>
            <Row style={{ marginBottom: 15 }}>
                <Col xs={18}>
                    <h5>Company</h5>
                    <InputPicker
                        name="companyPicker"
                        value={selectedCompany ? selectedCompany.value : null}
                        onSelect={(value, item) => {
                            setSelectedCompany({
                                label: String(item.label),
                                value: Number(value),
                                group: item.group,
                                created: item.created,
                                circles_customer_id: item.circles_customer_id,
                            });
                            setValue(
                                'reference_title',
                                removeCompanyNameSuffix(String(item.label)) ?? '',
                                { shouldDirty: true },
                            );
                        }}
                        onClean={() => {
                            setSelectedCompany(undefined);
                            setValue('reference_title', '', { shouldDirty: true });
                        }}
                        placeholder="Select"
                        groupBy="group"
                        data={circlesCustomers}
                        block
                        size="lg"
                    />
                </Col>
            </Row>
            <Row style={{ marginBottom: 20 }}>
                <Col xs={24}>
                    <Controller
                        name="permission_to_use"
                        defaultValue={false}
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                checked={field.value}
                                onChange={(_value, checked) => field.onChange(checked)}
                                disabled={
                                    !selectedCompany?.circles_customer_id ||
                                    createReference.isLoading
                                }
                            >
                                Permission to use as a reference
                            </Checkbox>
                        )}
                    />
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <span>
                        Reference background image will be streched by width to fit the slide. Use
                        dimension 2600 x 1000 (w x h) or similar if you want whole image to fit to
                        the reserved space on slide.
                    </span>
                </Col>
                <Col xs={12}>
                    <span>
                        Logo will be positioned in top left corner of the slide before customer
                        name.
                    </span>
                </Col>
            </Row>
            <Row style={{ marginTop: 10 }}>
                <Col xs={12}>
                    <div>
                        <Controller
                            name="image_file"
                            control={control}
                            render={({ field: { onChange } }) => (
                                <>
                                    <ImagePicker
                                        value={selectedBackgroundImage}
                                        label="Select Reference Image *"
                                        disabled={!selectedCompany}
                                        onFileSelect={file => {
                                            setSelectedBackgroundImage(file);
                                            // Update the form field value for validation
                                            onChange(file?.blobFile || null);
                                        }}
                                    />
                                    <ErrorParagraph>{errors?.image_file?.message}</ErrorParagraph>
                                </>
                            )}
                        />
                    </div>
                </Col>
                <Col xs={12}>
                    <div>
                        <ImagePicker
                            value={selectedLogo}
                            label="Select Logo"
                            disabled={!selectedCompany}
                            onFileSelect={file => setSelectedLogo(file)}
                        />
                    </div>
                </Col>
            </Row>
            <Row style={{ marginTop: 20 }}>
                <Col xs={24}>
                    <p>
                        <strong>Customer Reference Title</strong>
                    </p>
                    <Controller
                        name="reference_title"
                        defaultValue=""
                        control={control}
                        render={({ field }) => (
                            <>
                                <Input size="lg" as="textarea" rows={1} {...field} />
                                <FieldInputLength
                                    currentLength={watchFields.reference_title?.length}
                                    maxLength={referenceQuoteByFieldMaxLength}
                                />
                            </>
                        )}
                    />
                    <ErrorParagraph>{errors?.reference_title?.message}</ErrorParagraph>
                </Col>
            </Row>
            <Row style={{ marginTop: 20 }}>
                <Col xs={24}>
                    <p>
                        <strong>
                            Challenge<span style={{ color: 'red' }}>*</span>
                        </strong>
                    </p>
                    <Controller
                        name="challenge"
                        defaultValue=""
                        control={control}
                        render={({ field }) => (
                            <>
                                <Input size="lg" as="textarea" rows={4} {...field} />
                                <FieldInputLength
                                    currentLength={watchFields.challenge?.length}
                                    maxLength={referenceTextFieldMaxLength}
                                />
                            </>
                        )}
                    />
                    <ErrorParagraph>{errors?.challenge?.message}</ErrorParagraph>
                </Col>
            </Row>
            <Row>
                <Col xs={24}>
                    <p>
                        <strong>
                            Solution<span style={{ color: 'red' }}>*</span>
                        </strong>
                    </p>
                    <Controller
                        name="solution"
                        defaultValue=""
                        control={control}
                        render={({ field }) => (
                            <>
                                <Input size="lg" as="textarea" rows={4} {...field} />
                                <FieldInputLength
                                    currentLength={watchFields.solution?.length}
                                    maxLength={referenceTextFieldMaxLength}
                                />
                            </>
                        )}
                    />
                    <ErrorParagraph>{errors?.solution?.message}</ErrorParagraph>
                </Col>
            </Row>
            <Row style={{ marginBottom: 20 }}>
                <Col xs={24}>
                    <p>
                        <strong>Quote</strong>
                    </p>
                    <Controller
                        name="quote"
                        defaultValue=""
                        control={control}
                        render={({ field }) => (
                            <>
                                <Input
                                    size="lg"
                                    as="textarea"
                                    rows={4}
                                    {...field}
                                    placeholder="Quote"
                                />
                                <FieldInputLength
                                    currentLength={watchFields.quote?.length}
                                    maxLength={referenceQuoteFieldMaxLength}
                                />
                            </>
                        )}
                    />
                    <ErrorParagraph>{errors?.quote?.message}</ErrorParagraph>
                    <Row gutter={16}>
                        <Col xs={12}>
                            <Controller
                                name="quote_by"
                                defaultValue=""
                                control={control}
                                render={({ field }) => (
                                    <>
                                        <Whisper
                                            trigger="hover"
                                            placement="top"
                                            speaker={<Tooltip>Name of the quoted person</Tooltip>}
                                        >
                                            <Input
                                                size="md"
                                                as="textarea"
                                                maxLength={referenceQuoteByFieldMaxLength}
                                                rows={1}
                                                {...field}
                                                placeholder="Quoted by (Name)"
                                            />
                                        </Whisper>
                                        <FieldInputLength
                                            currentLength={watchFields.quote_by?.length}
                                            maxLength={referenceQuoteByFieldMaxLength}
                                        />
                                    </>
                                )}
                            />
                            <ErrorParagraph>{errors?.quote_by?.message}</ErrorParagraph>
                        </Col>
                        <Col xs={12}>
                            <Controller
                                name="quote_by_title"
                                defaultValue=""
                                control={control}
                                render={({ field }) => (
                                    <>
                                        <Whisper
                                            trigger="hover"
                                            placement="top"
                                            speaker={
                                                <Tooltip>
                                                    Title/job descriptions of the quoted person
                                                </Tooltip>
                                            }
                                        >
                                            <Input
                                                size="md"
                                                as="textarea"
                                                maxLength={referenceQuoteByFieldMaxLength}
                                                rows={1}
                                                {...field}
                                                placeholder="Quoted by (Title)"
                                            />
                                        </Whisper>
                                        <FieldInputLength
                                            currentLength={watchFields.quote_by_title?.length}
                                            maxLength={referenceQuoteByFieldMaxLength}
                                        />
                                    </>
                                )}
                            />
                            <ErrorParagraph>{errors?.quote_by_title?.message}</ErrorParagraph>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row>
                <Col xs={24}>
                    <p>
                        <strong>
                            <i>Speaker Notes</i>
                        </strong>
                        <span style={{ paddingLeft: 20 }}>
                            <Button
                                appearance="primary"
                                color="green"
                                size="xs"
                                style={{ width: 100 }}
                                onClick={() => onGenerateNotes()}
                                disabled={
                                    !selectedCompany?.circles_customer_id ||
                                    createReference.isLoading
                                }
                                loading={isReferenceNotesLoading}
                            >
                                Generate
                            </Button>
                        </span>
                    </p>
                    <Controller
                        name="notes"
                        defaultValue=""
                        control={control}
                        render={({ field }) => (
                            <>
                                <Input size="lg" as="textarea" rows={6} {...field} />
                            </>
                        )}
                    />
                    <ErrorParagraph>{errors?.notes?.message}</ErrorParagraph>
                </Col>
            </Row>
            <Row>
                <Col xs={24} style={{ padding: 15 }}>
                    <div style={{ textAlign: 'center' }}>
                        <Button
                            type="submit"
                            appearance="primary"
                            color="green"
                            style={{ width: 300 }}
                            loading={createReference.isLoading}
                        >
                            Add reference
                        </Button>
                    </div>
                </Col>
            </Row>
        </form>
    );
};
